/**
 * Filter operator definitions for the report builder.
 *
 * Maps field types to available comparison operators and provides
 * suffix conventions for encoding operator + value in the filter state.
 */

/** Operator identifiers used in filter state keys */
export type FilterOperator =
  | 'eq'
  | 'ne'
  | 'in'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'contains'
  | 'starts_with'
  | 'not_in'
  | 'is_empty'
  | 'is_not_empty';

export interface OperatorOption {
  key: FilterOperator;
  label: string;
  /** Longer description shown in dropdown menu */
  description?: string;
  /** Whether this operator requires a value input */
  requiresValue: boolean;
  /** Whether this operator requires two value inputs (range) */
  requiresRange: boolean;
}

/** All available operators with their display labels */
const ALL_OPERATORS: Record<FilterOperator, OperatorOption> = {
  eq: { key: 'eq', label: 'equals', requiresValue: true, requiresRange: false },
  ne: {
    key: 'ne',
    label: 'not equal',
    description: 'does not equal the value',
    requiresValue: true,
    requiresRange: false,
  },
  in: {
    key: 'in',
    label: 'one of',
    description: 'matches any of selected values',
    requiresValue: true,
    requiresRange: false,
  },
  gt: {
    key: 'gt',
    label: 'greater than',
    description: 'is greater than the value',
    requiresValue: true,
    requiresRange: false,
  },
  gte: {
    key: 'gte',
    label: 'at least',
    description: 'is greater than or equal to',
    requiresValue: true,
    requiresRange: false,
  },
  lt: {
    key: 'lt',
    label: 'less than',
    description: 'is less than the value',
    requiresValue: true,
    requiresRange: false,
  },
  lte: {
    key: 'lte',
    label: 'at most',
    description: 'is less than or equal to',
    requiresValue: true,
    requiresRange: false,
  },
  between: {
    key: 'between',
    label: 'between',
    description: 'is between two values',
    requiresValue: false,
    requiresRange: true,
  },
  contains: { key: 'contains', label: 'contains', requiresValue: true, requiresRange: false },
  starts_with: {
    key: 'starts_with',
    label: 'starts with',
    requiresValue: true,
    requiresRange: false,
  },
  not_in: {
    key: 'not_in',
    label: 'not one of',
    description: 'is not one of selected values',
    requiresValue: true,
    requiresRange: false,
  },
  is_empty: {
    key: 'is_empty',
    label: 'is empty',
    description: 'field has no value',
    requiresValue: false,
    requiresRange: false,
  },
  is_not_empty: {
    key: 'is_not_empty',
    label: 'is not empty',
    description: 'field has a value',
    requiresValue: false,
    requiresRange: false,
  },
};

/** Field type categories for operator assignment */
type FieldCategory = 'text' | 'keyword' | 'number' | 'date' | 'boolean' | 'select';

/** Operators available for each field category */
const OPERATORS_BY_CATEGORY: Record<FieldCategory, FilterOperator[]> = {
  text: ['eq', 'ne', 'in', 'not_in', 'contains', 'starts_with', 'is_empty', 'is_not_empty'],
  keyword: ['eq', 'ne', 'in', 'not_in', 'contains', 'starts_with', 'is_empty', 'is_not_empty'],
  select: ['eq', 'ne', 'in', 'not_in', 'is_empty', 'is_not_empty'],
  number: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'],
  date: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'],
  boolean: ['eq', 'is_empty', 'is_not_empty'],
};

/**
 * Determine the field category from a FieldOption's type info.
 */
export function getFieldCategory(
  controlType: string,
  reportDatatype?: string,
  fieldType?: string
): FieldCategory {
  if (controlType === 'boolean') return 'boolean';
  if (controlType === 'date') return 'date';
  if (controlType === 'number-range') return 'number';
  if (controlType === 'select') return 'select';
  if (controlType === 'multi-select') return 'select';

  const type = reportDatatype || fieldType || '';
  if (['integer', 'long', 'float', 'double', 'short'].includes(type)) return 'number';
  if (type === 'date') return 'date';
  if (type === 'boolean') return 'boolean';

  return 'text';
}

/**
 * Get available operators for a field category.
 */
export function getOperatorsForCategory(category: FieldCategory): OperatorOption[] {
  const keys = OPERATORS_BY_CATEGORY[category] || OPERATORS_BY_CATEGORY.text;
  return keys.map((k) => ALL_OPERATORS[k]);
}

/**
 * Get the default operator for a field category.
 */
export function getDefaultOperator(category: FieldCategory): FilterOperator {
  if (category === 'number' || category === 'date') return 'between';
  return 'eq';
}

/**
 * Get operator info by key.
 */
export function getOperatorInfo(op: FilterOperator): OperatorOption {
  return ALL_OPERATORS[op];
}

/**
 * Suffix used in the filter state key for a given operator.
 * Returns empty string for 'eq' (no suffix = equals).
 *
 * Filter state encoding:
 *   eq:           { fieldKey: value }
 *   ne:           { fieldKey__ne: value }
 *   gt:           { fieldKey__gt: value }
 *   gte:          { fieldKey__gte: value }
 *   lt:           { fieldKey__lt: value }
 *   lte:          { fieldKey__lte: value }
 *   between:      { fieldKey__gte: start, fieldKey__lte: end }
 *   contains:     { fieldKey__contains: value }
 *   starts_with:  { fieldKey__starts_with: value }
 *   in:           { fieldKey__in: [values] }
 *   not_in:       { fieldKey__not_in: [values] }
 *   is_empty:     { fieldKey__is_empty: true }
 *   is_not_empty: { fieldKey__is_not_empty: true }
 */
export function getFilterSuffix(op: FilterOperator): string {
  if (op === 'eq') return '';
  return `__${op}`;
}

/**
 * Parse a filter key into its base field key and operator.
 * e.g., "amount__gte" → { baseKey: "amount", operator: "gte" }
 *       "name" → { baseKey: "name", operator: "eq" }
 */
export function parseFilterKey(key: string): { baseKey: string; operator: FilterOperator } {
  const suffixes: FilterOperator[] = [
    'is_not_empty',
    'is_empty',
    'starts_with',
    'contains',
    'not_in',
    'in',
    'between',
    'gte',
    'lte',
    'gt',
    'lt',
    'ne',
  ];

  for (const suffix of suffixes) {
    const pattern = `__${suffix}`;
    if (key.endsWith(pattern)) {
      return { baseKey: key.slice(0, -pattern.length), operator: suffix };
    }
  }

  return { baseKey: key, operator: 'eq' };
}
